import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext } from "formik";
import XGSFormDate from "../../../../ui-components/form/date/xgsFormDate";
import LabeledSelectInput from "../../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import ImportantMessage, { ImportantMessageTheme } from "../../../../ui-components/molecules/important-message/importantMessage";
import { currentOrClosestBusinessDay } from "../../../../hooks/utils";
import { getDateClassName, getNonServiceDateNotification } from "../../../../services/pickups";
import { fromTimeOptions, toTimeOptions } from "../../../../services/common/time";
import { checkPickup } from "../../../../slices/pickup/pickupSlice";
import { commonSelector, getPickupDays } from "../../../../slices/common/commonSlice";
import { BolDocument } from "../../../../app/data/pickup-requests/bolModels";
import { BolReviewFormValues } from "../utils/bolReviewMappers";
import BolFieldWithAi from "./bolFieldWithAi";

const NEW_SHIPPER_ACCOUNT = 69;

export interface BolPickupWindowSectionProps {
  bolDoc: BolDocument;
  showExtractionHints?: boolean;
}

const BolPickupWindowSection: React.FC<BolPickupWindowSectionProps> = ({
  bolDoc,
  showExtractionHints = true,
}) => {
  const dispatch = useDispatch();
  const commonState = useSelector(commonSelector);
  const { values, setFieldValue } = useFormikContext<BolReviewFormValues>();
  const [nonServiceDayNotification, setNonServiceDayNotification] = useState<string | undefined>();

  const shipperZip = values.pickupFrom.zip?.trim() || "";
  const zipReady = shipperZip.length === 5;

  const getHolidayDates = () =>
    [...(commonState.shipperHolidays || []), ...(commonState.xgsHolidays || [])].map((h) => h.date);

  useEffect(() => {
    if (!zipReady) return;
    dispatch(getPickupDays(NEW_SHIPPER_ACCOUNT, shipperZip));
    dispatch(checkPickup(shipperZip, () => undefined, () => undefined));
  }, [dispatch, shipperZip, zipReady]);

  const checkNonServiceDateNotification = (date: string) => {
    setNonServiceDayNotification(
      getNonServiceDateNotification(date, commonState.pickupDays, commonState.shipperHolidays, commonState.xgsHolidays)
    );
  };

  const pickupDaysLoading =
    commonState.requestStarted && commonState.requestCreator === "GET_PICKUP_DAYS";

  return (
    <div className="xgs-captured-bol__row xgs-captured-bol__pickup-window">
      <div className="xgs-gray-area xgs-pickup__field xgs-captured-bol__pickup-window-inner">
        <div className="xgs-gray-area__header">Pickup Date and Time</div>
        <BolFieldWithAi label="Pickup Date" field={bolDoc.pickupWindow?.windowDate} required showExtractionHints={showExtractionHints}>
          <XGSFormDate
            className="xgs-pickup__field"
            name="pickupDate"
            label=""
            placeholder="Select date..."
            value={values.pickupDate}
            onDateChange={(date) => {
              setFieldValue("pickupDate", date);
              checkNonServiceDateNotification(date);
            }}
            onChange={() => null}
            minDate={new Date(currentOrClosestBusinessDay())}
            required={true}
            labelMode={LabelModes.column}
            disableWeekends={true}
            disabled={!zipReady || pickupDaysLoading}
            dayClassName={(date) =>
              getDateClassName(date, commonState.pickupDays, getHolidayDates())
            }
          />
        </BolFieldWithAi>
        {!zipReady && (
          <p className="xgs-captured-bol__pickup-window-hint">
            Enter shipper ZIP code to enable pickup date selection.
          </p>
        )}
        {nonServiceDayNotification && (
          <ImportantMessage theme={ImportantMessageTheme.BLUE} mix="xgs-pickup__message-row">
            {nonServiceDayNotification}
          </ImportantMessage>
        )}
        <div className="xgs-bol__return__availability">
          <div className="xgs-bol__return__availability__controls">
            <BolFieldWithAi label="Ready Time" field={bolDoc.pickupWindow?.windowStartTime} required showExtractionHints={showExtractionHints}>
              <LabeledSelectInput
                name="pickupReadyTime"
                label=""
                labelMode={LabelModes.column}
                value={
                  values.pickupReadyTime
                    ? { value: values.pickupReadyTime, label: values.pickupReadyTime }
                    : null
                }
                onValueChange={(v) => setFieldValue("pickupReadyTime", v?.value ? String(v.value) : "")}
                options={fromTimeOptions()}
                required={true}
                formik={true}
                placeholder="Ready..."
                isSearchable={false}
                menuPlacement="top"
              />
            </BolFieldWithAi>
            <div className="xgs-bol__return__availability__controls__delimiter">&mdash;</div>
            <BolFieldWithAi label="Close Time" field={bolDoc.pickupWindow?.windowEndTime} required showExtractionHints={showExtractionHints}>
              <LabeledSelectInput
                name="pickupCloseTime"
                label=""
                labelMode={LabelModes.column}
                value={
                  values.pickupCloseTime
                    ? { value: values.pickupCloseTime, label: values.pickupCloseTime }
                    : null
                }
                onValueChange={(v) => setFieldValue("pickupCloseTime", v?.value ? String(v.value) : "")}
                options={toTimeOptions(values.pickupReadyTime || "")}
                required={true}
                formik={true}
                placeholder="Close..."
                isSearchable={false}
                menuPlacement="top"
              />
            </BolFieldWithAi>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BolPickupWindowSection;
